import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import syncTemplatesService from '../services/syncTemplatesService';
import marketplaceService from '../services/marketplaceService';
import processingStepsService from '../services/processingStepsService';
import './AdminSyncTemplates.css';
import './PaymentPorterPage.css';

const emptyTemplate = { name: '', description: '', status: 'ACTIVE', code: '', imageurl: '', sourceMarketplaceId: '', targetMarketplaceId: '', usesMigrationToolkit: 'N', syncConnectOffline: 'N', docsPageId: '' };

const AdminSyncTemplateForm = () => {
    const navigate      = useNavigate();
    const { id }        = useParams();
    const isNew         = !id;

    const [form, setForm]                       = useState(emptyTemplate);
    const [marketplaceOptions, setMarketplaceOptions] = useState([]);
    const [loading, setLoading]                 = useState(!isNew);
    const [saving, setSaving]                   = useState(false);
    const [error, setError]                     = useState(null);

    const [tasks, setTasks]                     = useState([]);
    const [tasksLoading, setTasksLoading]       = useState(false);
    const [deleteConfirm, setDeleteConfirm]     = useState(null);
    const [activeTab, setActiveTab]             = useState('tasks');

    const [steps, setSteps]                     = useState([]);
    const [stepsLoading, setStepsLoading]       = useState(false);
    const [availableSteps, setAvailableSteps]   = useState([]);
    const [stepForm, setStepForm]               = useState(null); // null=closed, {}=new, {id,...}=edit
    const [stepSaving, setStepSaving]           = useState(false);
    const [stepDeleteConfirm, setStepDeleteConfirm] = useState(null);
    const [dragIndex, setDragIndex]             = useState(null);
    const [dragOverIndex, setDragOverIndex]     = useState(null);
    const [taskDragIndex, setTaskDragIndex]     = useState(null);
    const [taskDragOverIndex, setTaskDragOverIndex] = useState(null);

    useEffect(() => {
        marketplaceService.getAllSettings({})
            .then(mkt => {
                const opts = (mkt.settings || [])
                    .filter(m => m.SYSTEMACTIVE === 'Y')
                    .sort((a, b) => (a.SYSTEMNAME || '').localeCompare(b.SYSTEMNAME || ''));
                setMarketplaceOptions(opts);
            });

        processingStepsService.list()
            .then(data => setAvailableSteps(data || []))
            .catch(() => {});

        if (!isNew) {
            Promise.all([
                syncTemplatesService.getTemplate(id),
                syncTemplatesService.listTasks(id),
                syncTemplatesService.listProcessingSteps(id),
            ])
                .then(([tmpl, taskList, stepList]) => {
                    const t = tmpl.template || tmpl;
                    setForm({
                        name:                t.name || '',
                        description:         t.description || '',
                        status:              t.status || 'ACTIVE',
                        code:                t.code || '',
                        imageurl:            t.imageurl || '',
                        sourceMarketplaceId: t.sourceMarketplaceId?.toString() || '',
                        targetMarketplaceId: t.targetMarketplaceId?.toString() || '',
                        usesMigrationToolkit: t.usesMigrationToolkit === 'Y' ? 'Y' : 'N',
                        syncConnectOffline:  t.syncConnectOffline === 'Y' ? 'Y' : 'N',
                        docsPageId:          t.docsPageId || '',
                    });
                    setTasks(taskList || []);
                    setSteps(stepList || []);
                })
                .catch(() => setError('Failed to load template'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            if (isNew) {
                await syncTemplatesService.createTemplate(form);
            } else {
                await syncTemplatesService.updateTemplate(id, form);
            }
            navigate('/admin/sync-templates');
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const loadTasks = async () => {
        setTasksLoading(true);
        try {
            const data = await syncTemplatesService.listTasks(id);
            setTasks(data || []);
        } finally {
            setTasksLoading(false);
        }
    };

    // Renumber every task 1..N in the current (sequence) order and persist the ones that changed.
    const resequenceTasks = async () => {
        if (!tasks.length) return;
        const renumbered = tasks.map((t, i) => ({ ...t, sequence: i + 1 }));
        const changed = renumbered.filter((t, i) => t.sequence !== tasks[i].sequence);
        if (!changed.length) return;
        setTasksLoading(true);
        try {
            await Promise.all(changed.map(t => syncTemplatesService.updateTask(id, t.id, t)));
            setTasks(renumbered);
        } catch (err) {
            await loadTasks(); // resync on failure
        } finally {
            setTasksLoading(false);
        }
    };

    const handleTaskDragStart = (e, index) => {
        setTaskDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleTaskDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== taskDragOverIndex) setTaskDragOverIndex(index);
    };

    const handleTaskDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (taskDragIndex === null || taskDragIndex === dropIndex) {
            setTaskDragIndex(null);
            setTaskDragOverIndex(null);
            return;
        }
        const prev = tasks;
        const reordered = [...tasks];
        const [moved] = reordered.splice(taskDragIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        const renumbered = reordered.map((t, i) => ({ ...t, sequence: i + 1 }));
        setTasks(renumbered);
        setTaskDragIndex(null);
        setTaskDragOverIndex(null);
        // Persist only the tasks whose sequence changed.
        const oldSeq = new Map(prev.map(p => [p.id, p.sequence]));
        const changed = renumbered.filter(t => t.sequence !== oldSeq.get(t.id));
        try {
            await Promise.all(changed.map(t => syncTemplatesService.updateTask(id, t.id, t)));
        } catch (err) {
            await loadTasks(); // resync on failure
        }
    };

    const handleTaskDragEnd = () => {
        setTaskDragIndex(null);
        setTaskDragOverIndex(null);
    };

    const handleDeleteTask = async () => {
        if (!deleteConfirm) return;
        try {
            await syncTemplatesService.deleteTask(id, deleteConfirm.id);
            setTasks(prev => prev.filter(t => t.id !== deleteConfirm.id));
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleSaveStep = async () => {
        if (!stepForm?.processingStepId) return;
        setStepSaving(true);
        try {
            const picked = availableSteps.find(s => s.id === stepForm.processingStepId);
            if (stepForm.id) {
                await syncTemplatesService.updateProcessingStep(id, stepForm.id, stepForm);
                setSteps(prev => prev.map(s => s.id === stepForm.id
                    ? { ...s, processingStepId: stepForm.processingStepId, sequence: stepForm.sequence, name: picked?.name || s.name, description: picked?.description || s.description }
                    : s
                ));
            } else {
                const result = await syncTemplatesService.createProcessingStep(id, stepForm);
                const newStep = { id: result.id, processingStepId: stepForm.processingStepId, sequence: stepForm.sequence || 1, name: picked?.name || '', description: picked?.description || '' };
                setSteps(prev => [...prev, newStep].sort((a, b) => a.sequence - b.sequence || a.id - b.id));
            }
            setStepForm(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Save failed');
        } finally {
            setStepSaving(false);
        }
    };

    const handleDeleteStep = async () => {
        if (!stepDeleteConfirm) return;
        try {
            await syncTemplatesService.deleteProcessingStep(id, stepDeleteConfirm.id);
            setSteps(prev => prev.filter(s => s.id !== stepDeleteConfirm.id));
        } catch (err) {
            setError(err.response?.data?.message || 'Delete failed');
        } finally {
            setStepDeleteConfirm(null);
        }
    };

    const handleStepDragStart = (e, index) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleStepDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) setDragOverIndex(index);
    };

    const handleStepDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (dragIndex === null || dragIndex === dropIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        const reordered = [...steps];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        const renumbered = reordered.map((s, i) => ({ ...s, sequence: i + 1 }));
        setSteps(renumbered);
        setDragIndex(null);
        setDragOverIndex(null);
        // Persist changed sequences
        const changed = renumbered.filter((s, i) => s.sequence !== steps[i]?.sequence || s.id !== steps[i]?.id);
        await Promise.all(
            renumbered.map(s => syncTemplatesService.updateProcessingStep(id, s.id, s))
        );
    };

    const handleStepDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    if (loading) return <div className="ast-container"><div className="ast-loading">Loading…</div></div>;

    return (
        <div className="ast-container">
            <button className="pp-back-btn" onClick={() => navigate('/admin/sync-templates')}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Sync Templates
            </button>

            <div className="ast-header">
                <h1>{isNew ? 'New Template' : 'Edit Template'}</h1>
            </div>

            {error && <div className="ast-error">{error}</div>}

            <div className="ast-form-card">
                <div className="ast-form-group">
                    <label>Name <span style={{color:'#dc2626'}}>*</span></label>
                    <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Template name" />
                </div>
                <div className="ast-form-group">
                    <label>Description</label>
                    <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" />
                </div>
                <div className="ast-form-row">
                    <div className="ast-form-group">
                        <label>Code</label>
                        <input value={form.code} onChange={e => set('code', e.target.value)} placeholder="e.g. OPERA_MEWS" />
                    </div>
                    <div className="ast-form-group">
                        <label>Status</label>
                        <select value={form.status} onChange={e => set('status', e.target.value)}>
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                        </select>
                    </div>
                    {/* Y: projects from this template show a Toolkit button (the Migration Toolkit, working through
                        the project's own OPERA Cloud connection). */}
                    <div className="ast-form-group">
                        <label>Uses Migration Toolkit</label>
                        <select value={form.usesMigrationToolkit} onChange={e => set('usesMigrationToolkit', e.target.value)}>
                            <option value="N">N</option>
                            <option value="Y">Y</option>
                        </select>
                    </div>
                    {/* Y: projects from this template build the OFFLINE SyncConnect (no hub contact), so their
                        client card offers no Auto-update or Logs (V610). */}
                    <div className="ast-form-group">
                        <label>Offline SyncConnect</label>
                        <select value={form.syncConnectOffline} onChange={e => set('syncConnectOffline', e.target.value)}>
                            <option value="N">N</option>
                            <option value="Y">Y</option>
                        </select>
                    </div>
                </div>
                {/* The Confluence page (space HHD) its projects' Docs button opens, beside Refresh (V622). The page's
                    URL or its id; blank = no Docs button. */}
                <div className="ast-form-row">
                    <div className="ast-form-group" style={{ flex: 1 }}>
                        <label>Documentation page (Confluence)</label>
                        <input type="text" value={form.docsPageId} placeholder="https://hconnect.atlassian.net/wiki/spaces/HHD/pages/…"
                               onChange={e => set('docsPageId', e.target.value)} />
                    </div>
                </div>
                <div className="ast-form-row">
                    <div className="ast-form-group">
                        <label>Source System</label>
                        <select value={form.sourceMarketplaceId} onChange={e => set('sourceMarketplaceId', e.target.value)}>
                            <option value="">— none —</option>
                            {marketplaceOptions.map(m => (
                                <option key={m.MARKETPLACEID} value={m.MARKETPLACEID}>{m.SYSTEMNAME} [{m.MARKETPLACEID}]</option>
                            ))}
                        </select>
                    </div>
                    <div className="ast-form-group">
                        <label>Target System</label>
                        <select value={form.targetMarketplaceId} onChange={e => set('targetMarketplaceId', e.target.value)}>
                            <option value="">— none —</option>
                            {marketplaceOptions.map(m => (
                                <option key={m.MARKETPLACEID} value={m.MARKETPLACEID}>{m.SYSTEMNAME} [{m.MARKETPLACEID}]</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="ast-form-group">
                    <label>Image URL</label>
                    <input value={form.imageurl} onChange={e => set('imageurl', e.target.value)} placeholder="https://…" />
                </div>

                <div className="ast-form-actions">
                    <button className="pp-criteria-add-btn" onClick={() => navigate('/admin/sync-templates')} disabled={saving}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        Cancel
                    </button>
                    <button className="pp-rules-add-btn" onClick={handleSave} disabled={saving || !form.name.trim()}>
                        {saving
                        ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Saving…</>
                        : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{isNew ? 'Create' : 'Save'}</>
                    }
                    </button>
                </div>
            </div>

            {/* Tabbed section — only shown when editing an existing template */}
            {!isNew && (
                <div style={{ marginTop: '1.5rem' }}>
                    <div className="ast-tab-bar">
                        <button className={`ast-tab-btn${activeTab === 'tasks' ? ' ast-tab-btn-active' : ''}`} onClick={() => setActiveTab('tasks')}>Tasks</button>
                        <button className={`ast-tab-btn${activeTab === 'steps' ? ' ast-tab-btn-active' : ''}`} onClick={() => setActiveTab('steps')}>Processing Steps</button>
                    </div>

                    {activeTab === 'tasks' && (
                        <div className="ast-form-card">
                            <div className="ast-tasks-header">
                                <h3>Tasks</h3>
                                <button className="pp-rules-add-btn" onClick={() => navigate(`/admin/sync-templates/${id}/tasks/new`)}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                    Add
                                </button>
                            </div>

                            {tasksLoading ? (
                                <div className="ast-loading">Loading tasks…</div>
                            ) : (
                                <table className="ast-tasks-table">
                                    <thead>
                                        <tr>
                                            <th onClick={resequenceTasks} style={{ cursor: 'pointer' }} title="Click to resequence tasks starting at 1">#</th>
                                            <th>Name</th>
                                            <th>Phase</th>
                                            <th>Target</th>
                                            <th>Auto</th>
                                            <th>Repeatable</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tasks.length === 0 ? (
                                            <tr><td colSpan={7} className="ast-no-tasks">No tasks yet.</td></tr>
                                        ) : (
                                            tasks.map((task, index) => (
                                                <tr key={task.id}
                                                    draggable
                                                    onDragStart={e => handleTaskDragStart(e, index)}
                                                    onDragOver={e => handleTaskDragOver(e, index)}
                                                    onDrop={e => handleTaskDrop(e, index)}
                                                    onDragEnd={handleTaskDragEnd}
                                                    className={taskDragOverIndex === index && taskDragIndex !== index ? 'ast-drag-over' : ''}
                                                    style={{ opacity: taskDragIndex === index ? 0.4 : 1, cursor: 'grab' }}
                                                >
                                                    <td>{task.sequence}</td>
                                                    <td>
                                                        <div>{task.name}</div>
                                                        {task.description && <div className="ast-task-desc">{task.description}</div>}
                                                    </td>
                                                    <td>{task.phase || '—'}</td>
                                                    <td>{task.target || '—'}</td>
                                                    <td>{task.automaticYn}</td>
                                                    <td>{task.repeatable}</td>
                                                    <td>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => navigate(`/admin/sync-templates/${id}/tasks/${task.id}/edit`)}>
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                Edit
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={() => setDeleteConfirm({ id: task.id, name: task.name })}>
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === 'steps' && (
                        <div className="ast-form-card">
                            <div className="ast-tasks-header">
                                <h3>Processing Steps</h3>
                                <button className="pp-rules-add-btn" onClick={() => setStepForm({ processingStepId: '', sequence: steps.length + 1 })}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                    Add
                                </button>
                            </div>

                            {stepsLoading ? (
                                <div className="ast-loading">Loading…</div>
                            ) : (
                                <table className="ast-tasks-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '32px' }}></th>
                                            <th style={{ width: '48px' }}>#</th>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {steps.length === 0 ? (
                                            <tr><td colSpan={5} className="ast-no-tasks">No processing steps yet.</td></tr>
                                        ) : (
                                            steps.map((step, index) => (
                                                <tr
                                                    key={step.id}
                                                    draggable
                                                    onDragStart={e => handleStepDragStart(e, index)}
                                                    onDragOver={e => handleStepDragOver(e, index)}
                                                    onDrop={e => handleStepDrop(e, index)}
                                                    onDragEnd={handleStepDragEnd}
                                                    className={dragOverIndex === index && dragIndex !== index ? 'ast-drag-over' : ''}
                                                    style={{ opacity: dragIndex === index ? 0.4 : 1 }}
                                                >
                                                    <td className="ast-drag-handle" title="Drag to reorder">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>
                                                    </td>
                                                    <td>{step.sequence}</td>
                                                    <td>{step.name}</td>
                                                    <td style={{ color: '#6b7280' }}>{step.description || '—'}</td>
                                                    <td>
                                                        <div className="pp-criteria-row-actions">
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={() => setStepForm({ id: step.id, processingStepId: step.processingStepId, sequence: step.sequence })}>
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                Edit
                                                            </button>
                                                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={() => setStepDeleteConfirm({ id: step.id, name: step.name })}>
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Add/edit processing step modal */}
            {stepForm && (
                <div className="ast-modal-overlay">
                    <div className="ast-modal">
                        <h2>{stepForm.id ? 'Edit Processing Step' : 'Add Processing Step'}</h2>
                        <div className="ast-form-group">
                            <label>Processing Step <span style={{color:'#dc2626'}}>*</span></label>
                            <select
                                value={stepForm.processingStepId || ''}
                                onChange={e => setStepForm(f => ({ ...f, processingStepId: parseInt(e.target.value) || '' }))}
                                style={{ width: '100%' }}
                            >
                                <option value="">— select a step —</option>
                                {availableSteps.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}{s.description ? ` — ${s.description}` : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div className="ast-form-group">
                            <label>Sequence</label>
                            <input type="number" min="1" value={stepForm.sequence || 1} onChange={e => setStepForm(f => ({ ...f, sequence: parseInt(e.target.value) || 1 }))} />
                        </div>
                        <div className="ast-modal-actions">
                            <button className="pp-criteria-add-btn" onClick={() => setStepForm(null)} disabled={stepSaving}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                Cancel
                            </button>
                            <button className="pp-rules-add-btn" onClick={handleSaveStep} disabled={stepSaving || !stepForm.processingStepId}>
                                {stepSaving ? 'Saving…' : (stepForm.id ? 'Save' : 'Add')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete processing step confirm */}
            {stepDeleteConfirm && (
                <div className="ast-modal-overlay">
                    <div className="ast-modal ast-modal-sm">
                        <h2>Delete Processing Step</h2>
                        <p>Delete <strong>{stepDeleteConfirm.name}</strong>?</p>
                        <div className="ast-modal-actions">
                            <button className="pp-back-btn" onClick={() => setStepDeleteConfirm(null)}>Cancel</button>
                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={handleDeleteStep}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete task confirm */}
            {deleteConfirm && (
                <div className="ast-modal-overlay">
                    <div className="ast-modal ast-modal-sm">
                        <h2>Delete Task</h2>
                        <p>Delete <strong>{deleteConfirm.name}</strong>?</p>
                        <div className="ast-modal-actions">
                            <button className="pp-back-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={handleDeleteTask}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSyncTemplateForm;
